/*
  eslint-disable camelcase
*/
import Vue from 'vue';
import {
  ValidationProvider,
  ValidationObserver,
  extend,
  configure,
} from 'vee-validate';
import {
  alpha_dash,
  alpha_num,
  alpha_spaces,
  alpha,
  between,
  confirmed,
  digits,
  dimensions,
  email,
  ext,
  image,
  oneOf,
  integer,
  length,
  is_not,
  is,
  max,
  max_value,
  mimes,
  min,
  min_value,
  excluded,
  numeric,
  regex,
  required,
  required_if,
  size,
} from 'vee-validate/dist/rules';
import * as mime from 'mime-types';
import VueI18n from 'vue-i18n';
import { MIME_TYPES } from '@/helper/file';

export default function BootstrapVeeValidate(i18n: VueI18n): void {
  extend('alpha_dash', alpha_dash);
  extend('alpha_num', alpha_num);
  extend('alpha_spaces', alpha_spaces);
  extend('alpha', alpha);
  extend('between', between);
  extend('confirmed', confirmed);
  extend('digits', digits);
  extend('dimensions', dimensions);
  extend('email', email);
  extend('ext', ext);
  extend('image', image);
  extend('oneOf', oneOf);
  extend('integer', integer);
  extend('length', length);
  extend('is_not', is_not);
  extend('is', is);
  extend('max', max);
  extend('max_value', max_value);
  extend('mimes', mimes);
  extend('min', min);
  extend('min_value', min_value);
  extend('excluded', excluded);
  extend('numeric', numeric);
  extend('regex', regex);
  extend('required', required);
  extend('required_if', required_if);
  extend('size', size);

  extend('not_in_locations', (value, values) => {
    const result = values.every(
      (i: string) => i.split('-')[0] !== i.split('-')[1],
    );
    return result || 'This location has been existed';
  });

  extend(
    'is_not_head',
    (value, [name, id]: any): string | boolean =>
      (value && value !== id && name !== 'Incorrect') ||
      'Incorrect head office',
  );

  extend('date_expire_valid', (value, [startDate]: any) => {
    const end = new Date(value);
    const start = new Date(startDate);
    return end > start || 'Invalid date';
  });

  configure({
    defaultMessage: (field, values): string => {
      // eslint-disable-next-line no-underscore-dangle
      values._field_ = field;
      // eslint-disable-next-line no-underscore-dangle
      return String(i18n.t(`validation.${values._rule_}`, values));
    },
  });

  extend('file_attachment', (value, fileTypes): boolean =>
    fileTypes.some(
      (fileType) =>
        fileType in MIME_TYPES &&
        MIME_TYPES[fileType].includes(mime.lookup(value.name)),
    ),
  );

  extend(
    'max_size_upload_via_mb',
    (value, [max_size]: any): boolean | string => {
      const size_of_file = Math.floor((value.size / (1024 * 1024)) * 100) / 100;
      return (
        size_of_file < max_size ||
        String(i18n.t('validation.max_size_file', { max_size }))
      );
    },
  );

  extend('banned_file_type', (value, fileTypes): boolean =>
    fileTypes.every(
      (fileType) =>
        fileType in MIME_TYPES &&
        !MIME_TYPES[fileType].includes(mime.lookup(value.name)),
    ),
  );

  extend('strong_password', (value: string): boolean | any => {
    value = String(value) || '';
    return !(
      value.length < 8 ||
      !value.match(/[A-Z]/g) ||
      !value.match(/[a-z]/g) ||
      !value.match(/[0-9]/g)
    );
  });

  Vue.component('ValidationProvider', ValidationProvider);
  Vue.component('ValidationObserver', ValidationObserver);
}
